import { useEffect, useState, useRef } from 'react';
import {
  Users,
  Tags,
  Paperclip,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  LinkIcon,
  StickyNote,
  Check,
  Calendar,
} from 'lucide-react';
import { apiGet, apiPatch } from '../lib/api';
import { getInitials } from '../lib/people';
import { LinkPicker } from './LinkPicker';
import { FileUpload } from './FileUpload';
import { TimeSelect } from './TimeSelect';

interface Person {
  id: string;
  name: string;
  email: string | null;
  role: string | null;
  department: string | null;
}

interface MeetingInfoSidebarProps {
  instanceId: string;
  seriesId?: string;
  attendeeIds: string[];
  defaultAttendeeIds?: string[];
  onAttendeeIdsChange: (ids: string[]) => void;
  meetingDate?: string;
  meetingTime?: string | null;
  durationMinutes?: number | null;
  onDateTimeChange?: (fields: { meeting_date?: string; meeting_time?: string | null; duration_minutes?: number | null }) => void;
}

export function MeetingInfoSidebar({
  instanceId,
  seriesId,
  attendeeIds,
  defaultAttendeeIds = [],
  onAttendeeIdsChange,
  meetingDate,
  meetingTime,
  durationMinutes,
  onDateTimeChange,
}: MeetingInfoSidebarProps) {
  const [dateTimeOpen, setDateTimeOpen] = useState(true);
  const [attendeesOpen, setAttendeesOpen] = useState(true);
  const [tagsOpen, setTagsOpen] = useState(true);
  const [linksOpen, setLinksOpen] = useState(true);
  const [notesOpen, setNotesOpen] = useState(true);
  const [seriesAttachmentsOpen, setSeriesAttachmentsOpen] = useState(false);
  const [seriesAttachmentCount, setSeriesAttachmentCount] = useState(0);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(0);

  const [allPeople, setAllPeople] = useState<Person[]>([]);
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const addAttendeeRef = useRef<HTMLDivElement>(null);
  const attendeeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiGet<Person[]>('/people').then(setAllPeople).catch(console.error);
  }, []);

  async function patchAttendees(newIds: string[]) {
    const prevIds = attendeeIds;
    onAttendeeIdsChange(newIds);
    try {
      await apiPatch(`/meeting-instances/${instanceId}`, { attendee_ids: newIds });
    } catch (err) {
      console.error('Failed to save attendees:', err);
      onAttendeeIdsChange(prevIds);
    }
  }

  function addAttendee(personId: string) {
    if (attendeeIds.includes(personId)) return;
    setShowAddAttendee(false);
    setAttendeeSearch('');
    patchAttendees([...attendeeIds, personId]);
  }

  function removeAttendee(personId: string) {
    patchAttendees(attendeeIds.filter(id => id !== personId));
  }

  function toggleDefaultAttendee(personId: string, isPresent: boolean) {
    if (isPresent) {
      // Remove from attendees (mark absent)
      patchAttendees(attendeeIds.filter(id => id !== personId));
    } else {
      // Add to attendees (mark present)
      patchAttendees([...attendeeIds, personId]);
    }
  }

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (addAttendeeRef.current && !addAttendeeRef.current.contains(e.target as Node)) {
        setShowAddAttendee(false);
        setAttendeeSearch('');
      }
    }
    if (showAddAttendee) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [showAddAttendee]);

  useEffect(() => {
    if (showAddAttendee && attendeeInputRef.current) {
      attendeeInputRef.current.focus();
    }
  }, [showAddAttendee]);

  const peopleMap = new Map(allPeople.map(p => [p.id, p]));
  const hasDefaults = defaultAttendeeIds.length > 0;

  // Split attendees into default vs additional
  const defaultAttendeePeople = defaultAttendeeIds
    .map(id => peopleMap.get(id))
    .filter((p): p is Person => p !== undefined);

  const additionalAttendees = attendeeIds
    .filter(id => !defaultAttendeeIds.includes(id))
    .map(id => peopleMap.get(id))
    .filter((p): p is Person => p !== undefined);

  // When no defaults, show all attendees in the old flat list
  const allAttendees = attendeeIds
    .map(id => peopleMap.get(id))
    .filter((p): p is Person => p !== undefined);

  const totalCount = hasDefaults
    ? defaultAttendeePeople.length + additionalAttendees.length
    : allAttendees.length;

  // Available people for the "Add" dropdown: exclude defaults (managed via checkboxes) and already-added people
  const availablePeople = allPeople.filter(
    p => !attendeeIds.includes(p.id) &&
      !defaultAttendeeIds.includes(p.id) &&
      (attendeeSearch === '' || p.name.toLowerCase().includes(attendeeSearch.toLowerCase()))
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide flex-1">Info</span>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {/* Date & Time */}
        <SidebarSection
          title="Date & Time"
          icon={<Calendar size={14} />}
          isOpen={dateTimeOpen}
          onToggle={() => setDateTimeOpen(!dateTimeOpen)}
        >
          <div className="space-y-2">
            <div>
              <label className="block text-[10px] text-text-muted mb-0.5">Date</label>
              <input
                type="date"
                value={meetingDate ?? ''}
                onChange={(e) => onDateTimeChange?.({ meeting_date: e.target.value })}
                className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary focus:outline-none focus:border-accent"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-text-muted mb-0.5">Time</label>
                <TimeSelect
                  value={meetingTime ?? ''}
                  onChange={(v) => onDateTimeChange?.({ meeting_time: v || null })}
                  className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary focus:outline-none focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-[10px] text-text-muted mb-0.5">Duration</label>
                <select
                  value={durationMinutes ?? ''}
                  onChange={(e) => onDateTimeChange?.({ duration_minutes: e.target.value ? Number(e.target.value) : null })}
                  className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary focus:outline-none focus:border-accent"
                >
                  <option value="">—</option>
                  <option value="15">15 min</option>
                  <option value="30">30 min</option>
                  <option value="45">45 min</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
            </div>
          </div>
        </SidebarSection>

        {/* Attendees */}
        <SidebarSection
          title="Attendees"
          icon={<Users size={14} />}
          isOpen={attendeesOpen}
          onToggle={() => setAttendeesOpen(!attendeesOpen)}
          count={totalCount}
        >
          <div className="space-y-1.5">
            {hasDefaults ? (
              <>
                {/* Regular Attendees — checkboxes for defaults */}
                {defaultAttendeePeople.length > 0 && (
                  <div className="space-y-0.5">
                    <p className="text-[10px] text-text-muted uppercase tracking-wide mb-1">Regular</p>
                    {defaultAttendeePeople.map(person => {
                      const isPresent = attendeeIds.includes(person.id);
                      return (
                        <button
                          key={person.id}
                          onClick={() => toggleDefaultAttendee(person.id, isPresent)}
                          className="flex items-center gap-2 w-full py-1 px-1 rounded hover:bg-bg-hover transition-colors text-left"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                            isPresent
                              ? 'bg-accent border-accent text-white'
                              : 'border-border bg-bg-input'
                          }`}>
                            {isPresent && <Check size={10} />}
                          </div>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold flex-shrink-0 ${
                            isPresent ? 'bg-accent/20 text-accent' : 'bg-bg-hover text-text-muted'
                          }`}>
                            {getInitials(person.name)}
                          </div>
                          <span className={`text-xs truncate flex-1 transition-colors ${
                            isPresent ? 'text-text-primary' : 'text-text-muted line-through'
                          }`}>
                            {person.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Additional Attendees — non-default people */}
                {additionalAttendees.length > 0 && (
                  <div className="space-y-0.5 mt-2">
                    <p className="text-[10px] text-text-muted uppercase tracking-wide mb-1">Additional</p>
                    {additionalAttendees.map(person => (
                      <div
                        key={person.id}
                        className="flex items-center gap-2 group py-1 px-1 rounded hover:bg-bg-hover transition-colors"
                      >
                        <div className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                          {getInitials(person.name)}
                        </div>
                        <span className="text-sm text-text-primary truncate flex-1">
                          {person.name}
                        </span>
                        <button
                          onClick={() => removeAttendee(person.id)}
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-text-muted hover:text-danger transition-all flex-shrink-0"
                          title="Remove attendee"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* No defaults — flat list (original behavior) */}
                {allAttendees.length === 0 && (
                  <p className="text-xs text-text-muted py-1">No attendees added</p>
                )}
                {allAttendees.map(person => (
                  <div
                    key={person.id}
                    className="flex items-center gap-2 group py-1 px-1 rounded hover:bg-bg-hover transition-colors"
                  >
                    <div className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                      {getInitials(person.name)}
                    </div>
                    <span className="text-sm text-text-primary truncate flex-1">
                      {person.name}
                    </span>
                    <button
                      onClick={() => removeAttendee(person.id)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-text-muted hover:text-danger transition-all flex-shrink-0"
                      title="Remove attendee"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </>
            )}

            {/* Add attendee dropdown */}
            <div className="relative" ref={addAttendeeRef}>
              {showAddAttendee ? (
                <div>
                  <input
                    ref={attendeeInputRef}
                    type="text"
                    value={attendeeSearch}
                    onChange={e => setAttendeeSearch(e.target.value)}
                    placeholder="Search people..."
                    className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                    onKeyDown={e => {
                      if (e.key === 'Escape') {
                        setShowAddAttendee(false);
                        setAttendeeSearch('');
                      }
                    }}
                  />
                  {availablePeople.length > 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-36 overflow-y-auto bg-bg-surface border border-border rounded-lg shadow-lg">
                      {availablePeople.map(person => (
                        <button
                          key={person.id}
                          onClick={() => addAttendee(person.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-bg-hover transition-colors"
                        >
                          <div className="w-5 h-5 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[9px] font-semibold flex-shrink-0">
                            {getInitials(person.name)}
                          </div>
                          <span className="text-xs text-text-primary truncate">{person.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {availablePeople.length === 0 && attendeeSearch && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-bg-surface border border-border rounded-lg shadow-lg px-3 py-2">
                      <span className="text-xs text-text-muted">No matching people</span>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowAddAttendee(true)}
                  className="flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors py-1"
                >
                  <Plus size={12} />
                  Add attendee
                </button>
              )}
            </div>
          </div>
        </SidebarSection>

        {/* Tags / Links */}
        <SidebarSection
          title="Links"
          icon={<LinkIcon size={14} />}
          isOpen={linksOpen}
          onToggle={() => setLinksOpen(!linksOpen)}
        >
          <LinkPicker
            sourceType="meeting_instance"
            sourceId={instanceId}
            relationship="related"
            placeholder="Link items..."
          />
        </SidebarSection>

        {/* Tags */}
        <SidebarSection
          title="Tags"
          icon={<Tags size={14} />}
          isOpen={tagsOpen}
          onToggle={() => setTagsOpen(!tagsOpen)}
        >
          <LinkPicker
            sourceType="meeting_instance"
            sourceId={instanceId}
            relationship="tagged"
            allowedTargetTypes={['tag']}
            placeholder="Add tags..."
          />
        </SidebarSection>

        {/* Notes */}
        <SidebarSection
          title="Notes"
          icon={<StickyNote size={14} />}
          isOpen={notesOpen}
          onToggle={() => setNotesOpen(!notesOpen)}
        >
          <LinkPicker
            sourceType="meeting_instance"
            sourceId={instanceId}
            relationship="related"
            allowedTargetTypes={['note']}
            placeholder="Link notes..."
          />
        </SidebarSection>

        {/* Series Attachments — only for series meetings */}
        {seriesId && (
          <SidebarSection
            title="Series Attachments"
            icon={<Paperclip size={14} />}
            isOpen={seriesAttachmentsOpen}
            onToggle={() => setSeriesAttachmentsOpen(!seriesAttachmentsOpen)}
            count={seriesAttachmentCount}
          >
            <FileUpload
              parentType="meeting_series"
              parentId={seriesId}
              onCountChange={setSeriesAttachmentCount}
              asOfDate={meetingDate}
              meetingDate={meetingDate}
              readOnly
            />
          </SidebarSection>
        )}

        {/* Attachments */}
        <SidebarSection
          title="Attachments"
          icon={<Paperclip size={14} />}
          isOpen={attachmentsOpen}
          onToggle={() => setAttachmentsOpen(!attachmentsOpen)}
          count={attachmentCount}
        >
          <FileUpload
            parentType="meeting"
            parentId={instanceId}
            onCountChange={setAttachmentCount}
            seriesId={seriesId}
            meetingDate={meetingDate}
          />
        </SidebarSection>
      </div>
    </div>
  );
}

function SidebarSection({
  title,
  icon,
  isOpen,
  onToggle,
  count,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border pb-3 mb-3 last:border-b-0 last:pb-0 last:mb-0">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full text-left py-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wide hover:text-text-primary transition-colors"
      >
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {icon}
        <span className="flex-1">{title}</span>
        {count !== undefined && count > 0 && (
          <span className="text-[10px] font-normal text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">
            {count}
          </span>
        )}
      </button>
      {isOpen && <div className="mt-2">{children}</div>}
    </div>
  );
}

